/* eslint no-console: 0 */

import { EventEmitter } from 'events';

import { Message, Entry, type FriendConfig, type FriendRoomGameType, type GuobiaoConfig } from '../server/protocol';
import { getOrCreatePlayerId, storePlayerId } from './player-id';
import { getLoginToken } from './user';

export interface Game {
  gameId: string;
  playerId: string;
  authoritative: boolean;
}

export class BaseClient {
  private ws: WebSocket | null = null;
  private game: Game | null = null;
  private events: EventEmitter = new EventEmitter();
  private pending: Array<Entry> | null = null;
  private localPlayerId: string;

  constructor() {
    this.events.setMaxListeners(50);
    this.localPlayerId = getOrCreatePlayerId();
  }

  new(url: string, options: { gameType?: FriendRoomGameType; friendConfig?: FriendConfig; guobiaoConfig?: GuobiaoConfig } = {}): void {
    this.connect(url, () => {
      const loginToken = getLoginToken();
      this.send({
        type: 'NEW',
        playerId: this.localPlayerId,
        gameType: options.gameType,
        friendConfig: options.friendConfig,
        guobiaoConfig: options.guobiaoConfig,
        loginToken: loginToken ?? undefined,
      });
    });
  }

  join(url: string, gameId: string, options: { guobiaoConfig?: GuobiaoConfig } = {}): void {
    this.connect(url, () => {
      const loginToken = getLoginToken();
      this.send({
        type: 'JOIN',
        gameId,
        playerId: this.localPlayerId,
        guobiaoConfig: options.guobiaoConfig,
        loginToken: loginToken ?? undefined,
      });
    });
  }

  disconnect(): void {
    this.ws?.close();
  }

  private connect(url: string, start: () => void): void {
    if (this.ws) {
      return;
    }
    this.ws = new WebSocket(url);
    this.ws.onopen = start;
    this.ws.onclose = this.onClose.bind(this);

    this.ws.onmessage = event => {
      const message = JSON.parse(event.data as string) as Message;
      // console.log('recv', message);
      this.onMessage(message);
    };
  }

  on(what: 'connect', handler: (game: Game, isFirst: boolean) => void): void;
  on(what: 'disconnect', handler: (game: Game | null) => void): void;
  on(what: 'update', handler: (things: Array<Entry>, full: boolean) => void): void;
  on(what: 'serverError', handler: (error: string) => void): void;
  on(what: 'actionAck', handler: (ack: Extract<Message, { type: 'ACTION_ACK' }>) => void): void;
  on(what: 'friendConfigSetAck', handler: (msg: Extract<Message, { type: 'FRIEND_CONFIG_SET_ACK' }>) => void): void;
  on(what: 'bloodConfigSetAck', handler: (msg: Extract<Message, { type: 'BLOOD_CONFIG_SET_ACK' }>) => void): void;
  on(what: 'guobiaoConfigSetAck', handler: (msg: Extract<Message, { type: 'GUOBIAO_CONFIG_SET_ACK' }>) => void): void;
  on(what: 'aiTemplates', handler: (msg: Extract<Message, { type: 'AI_TEMPLATES' }>) => void): void;
  on(what: 'aiTemplateSaved', handler: (msg: Extract<Message, { type: 'AI_TEMPLATE_SAVED' }>) => void): void;
  on(what: 'aiTemplateDeleted', handler: (msg: Extract<Message, { type: 'AI_TEMPLATE_DELETED' }>) => void): void;
  on(what: 'aiTemplateDefaultSet', handler: (msg: Extract<Message, { type: 'AI_TEMPLATE_DEFAULT_SET' }>) => void): void;
  on(what: 'aiHistory', handler: (msg: Extract<Message, { type: 'AI_HISTORY' }>) => void): void;
  on(what: 'aiPreview', handler: (msg: Extract<Message, { type: 'AI_PREVIEW' }>) => void): void;
  on(what: 'aiDelta', handler: (msg: Extract<Message, { type: 'AI_DELTA' }>) => void): void;
  on(what: 'aiResult', handler: (msg: Extract<Message, { type: 'AI_RESULT' }>) => void): void;
  on(what: 'aiError', handler: (msg: Extract<Message, { type: 'AI_ERROR' }>) => void): void;

  on(what: string, handler: (...args: any[]) => void): void {
    this.events.on(what, handler);
  }

  transaction(func: () => void): void {
    this.pending = [];
    try {
      func();
      if (this.pending !== null && this.pending.length > 0) {
        const filtered = this.filterOutgoingEntries(this.pending);
        if (filtered.length > 0) {
          this.send({ type: 'UPDATE', entries: filtered, full: false });
        }
      }
    } finally {
      this.pending = null;
    }
  }

  update(entries: Array<Entry>): void {
    const filtered = this.filterOutgoingEntries(entries);
    if (filtered.length === 0) {
      return;
    }
    if (this.pending !== null) {
      this.pending.push(...filtered);
    } else {
      this.send({ type: 'UPDATE', entries: filtered, full: false });
    }
  }

  protected send(message: Message): void {
    if (!this.open()) {
      return;
    }
    // console.log('send', message);
    const data = JSON.stringify(message);
    this.ws!.send(data);
  }

  private open(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  connected(): boolean {
    return this.open() && this.game !== null;
  }

  playerId(): string {
    return this.game?.playerId ?? this.localPlayerId;
  }

  gameId(): string | null {
    return this.game?.gameId ?? null;
  }

  isAuthoritative(): boolean {
    return this.game?.authoritative ?? false;
  }

  setLocalGame(game: Game | null): void {
    this.game = game;
  }

  private filterOutgoingEntries(entries: Array<Entry>): Array<Entry> {
    if (!this.isAuthoritative()) {
      return entries;
    }
    // In authoritative mode, the server is the only source of truth for these
    // collections. Clients must use ACTION instead.
    const forbidden = new Set<string>(['match', 'things', 'blood', 'bloodAction', 'gb', 'gbAction']);
    return entries.filter(([kind]) => !forbidden.has(kind));
  }

  private onClose(): void {
    this.ws = null;
    const game = this.game;
    this.game = null;
    this.events.emit('disconnect', game);
  }

  private onMessage(message: Message): void {
    switch (message.type) {
      case 'JOINED':
        this.localPlayerId = message.playerId;
        storePlayerId(message.playerId);
        this.game = {
          gameId: message.gameId,
          playerId: message.playerId,
          authoritative: !!message.authoritative,
        };
        this.events.emit('connect', this.game, message.isFirst);
        break;

      case 'UPDATE':
        this.events.emit('update', message.entries, message.full);
        break;

      case 'ERROR':
        this.events.emit('serverError', message.error);
        break;

      case 'ACTION_ACK':
        this.events.emit('actionAck', message);
        break;

      case 'FRIEND_CONFIG_SET_ACK':
        this.events.emit('friendConfigSetAck', message);
        break;

      case 'BLOOD_CONFIG_SET_ACK':
        this.events.emit('bloodConfigSetAck', message);
        break;

      case 'GUOBIAO_CONFIG_SET_ACK':
        this.events.emit('guobiaoConfigSetAck', message);
        break;

      case 'AI_TEMPLATES':
        this.events.emit('aiTemplates', message);
        break;

      case 'AI_TEMPLATE_SAVED':
        this.events.emit('aiTemplateSaved', message);
        break;

      case 'AI_TEMPLATE_DELETED':
        this.events.emit('aiTemplateDeleted', message);
        break;

      case 'AI_TEMPLATE_DEFAULT_SET':
        this.events.emit('aiTemplateDefaultSet', message);
        break;

      case 'AI_HISTORY':
        this.events.emit('aiHistory', message);
        break;

      case 'AI_PREVIEW':
        this.events.emit('aiPreview', message);
        break;

      case 'AI_DELTA':
        this.events.emit('aiDelta', message);
        break;

      case 'AI_RESULT':
        this.events.emit('aiResult', message);
        break;

      case 'AI_ERROR':
        this.events.emit('aiError', message);
        break;
    }
  }
}
